/**
 * Transient confirmations. Used for "saved" and for failures that do not belong
 * inline — never for anything the user has to act on, which goes in a banner
 * where it stays put.
 */

import { useCallback, useMemo, useState, type ReactNode } from 'react'
import { ToastContext, type ToastApi } from './toast-context'

interface Toast {
  id: number
  tone: 'success' | 'error'
  title: string
  body?: string
  note?: string
}

let nextId = 1

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])

  const push = useCallback((tone: Toast['tone'], title: string, body?: string, note?: string) => {
    const id = nextId++
    setToasts((current) => [...current, { id, tone, title, body, note }])
    // Errors and notes linger: they are more likely to matter, and more likely
    // to be read after the user has looked away from the screen.
    const ttl = tone === 'error' || note ? 8000 : 3500
    window.setTimeout(() => {
      setToasts((current) => current.filter((toast) => toast.id !== id))
    }, ttl)
  }, [])

  const api = useMemo<ToastApi>(
    () => ({
      success: (title, body, note) => push('success', title, body, note),
      error: (title, body) => push('error', title, body),
    }),
    [push],
  )

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className="toasts" role="status" aria-live="polite">
        {toasts.map((toast) => (
          <div key={toast.id} className={`toast toast--${toast.tone}`}>
            <div className="toast__title">{toast.title}</div>
            {toast.body ? <div className="toast__body">{toast.body}</div> : null}
            {toast.note ? <div className="toast__note">{toast.note}</div> : null}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  )
}
